import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { catHex, catIcon } from './meta';

interface Props {
  byCategory: Record<string, number>;
  total: number;
  currency: string;
}

export function CategoryDonut({ byCategory, total, currency }: Props) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  const R = 52;
  const C = 2 * Math.PI * R;
  const cx = 80;
  const cy = 80;

  const entries = Object.entries(byCategory)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);

  let cumPct = 0;
  const segments = entries.map(([cat, amt]) => {
    const pct = amt / total;
    const gap = entries.length > 1 ? 1.5 : 0;
    const dash = Math.max(pct * C - gap, 0);
    const offset = C * (1 - cumPct);
    cumPct += pct;
    return { cat, amt, pct, dash, offset, icon: catIcon(cat), hex: catHex(cat) };
  });

  const active = hovered ? segments.find(s => s.cat === hovered) : null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 shadow-card">
      <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400 mb-4">{t('expenses.byCategory')}</p>
      <div className="flex flex-col xl:flex-row xl:items-center gap-4 xl:gap-5">
        {/* SVG donut */}
        <div className="relative shrink-0 mx-auto xl:mx-0 w-[140px] h-[140px] xl:w-[160px] xl:h-[160px]">
          <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
            <circle cx={cx} cy={cy} r={R} fill="none"
              stroke="rgba(0,0,0,0.06)" strokeWidth={20} />
            {segments.map(s => (
              <circle
                key={s.cat}
                cx={cx} cy={cy} r={R}
                fill="none"
                stroke={s.hex}
                strokeWidth={hovered === s.cat ? 24 : 20}
                strokeDasharray={`${s.dash} ${C - s.dash}`}
                strokeDashoffset={s.offset}
                strokeLinecap="butt"
                className="cursor-pointer"
                style={{ transition: 'stroke-width 120ms ease, opacity 120ms ease', opacity: hovered && hovered !== s.cat ? 0.3 : 1 }}
                onMouseEnter={() => setHovered(s.cat)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {active ? (
              <>
                <p className="font-mono font-bold text-base leading-none tabular-nums text-slate-900 dark:text-slate-100"
                   style={{ color: active.hex }}>
                  {active.amt.toFixed(0)}
                </p>
                <p className="font-mono text-[10px] text-slate-400 mt-0.5">{(active.pct * 100).toFixed(0)}%</p>
              </>
            ) : (
              <>
                <p className="font-mono text-[10px] text-slate-400 leading-none mb-1">{currency}</p>
                <p className="font-mono font-bold text-3xl leading-none tabular-nums tracking-tight text-slate-900 dark:text-slate-100">
                  {total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total.toFixed(0)}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 min-w-0 space-y-2">
          {segments.map(s => (
            <div
              key={s.cat}
              className="flex items-center gap-2 cursor-default"
              onMouseEnter={() => setHovered(s.cat)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.hex }} />
              <span className="text-xs text-slate-600 dark:text-slate-400 flex-1 min-w-0 truncate">{t(`expenses.categories.${s.cat}` as Parameters<typeof t>[0])}</span>
              <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200 shrink-0 tabular-nums">
                {currency} {s.amt.toFixed(0)}
              </span>
              <span className="font-mono text-[10px] text-slate-400 w-8 text-right shrink-0 tabular-nums">
                {(s.pct * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
